import { grpc } from "@improbable-eng/grpc-web";
import { mdiWindowClose } from "@mdi/js";
import Icon from "@mdi/react";
import React, { useCallback, useEffect, useState } from "react";
import Modal from "react-modal";
import styled from "styled-components/macro";
import { getErrorDetail, idpc } from "../api";
import { AuthContext, Configuration, User } from "../generated/idp/api/idp";
import { AuthPasswordExtension, ChangePassword } from "../login/Password";
import { Spinner } from "../Spinner";
import { Authenticator } from "./Authenticator";
import { EmailManager } from "./Email";
import { Nav } from "./Nav";
import { Button } from "../Button";
import { ConfirmationModal } from "./ConfirmationModal";

const FlexRow = styled.div`
  display: flex;
  flex-flow: row;
`;

const Title = styled.h2`
  margin-bottom: 6px;
  margin-top: 25px;
`;

export function Account(props: {
  onLogout: () => void;
  authenticate: (a: AuthContext) => void;
}): JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [passwordModalOpen, setPasswordModalOpen] = useState<boolean>(false);
  const [enhancedSecurityModalOpen, setEnhancedSecurityModalOpen] = useState<boolean>(false);
  const [pendingEnhancedSecurityState, setPendingEnhancedSecurityState] = useState<boolean>(false);
  const [configuration, setConfiguration] = useState<Configuration>(Configuration.create({}));
  const authenticate = props.authenticate;

  const fetchUser = useCallback(async () => {
    try {
      const res = await idpc.GetUser({});
      setUser(res.user ?? null);
    } catch (err) {
      if (err.code === grpc.Code.Unauthenticated) {
        authenticate({
          $type: "idp.AuthContext",
          authenticationContextClass: [],
          maxAgeSeconds: 99999999,
        });
      }
      let ia = getErrorDetail(err, AuthContext);
      if (ia !== null) {
        authenticate(ia);
      }
    }
  }, [authenticate]);

  useEffect(() => {
    let ignore = false;
    const fetchData = async () => {
      if (!ignore) {
        await fetchUser();
      }
    };
    fetchData();
    return () => {
      ignore = true;
    };
  }, [fetchUser]);

  useEffect(() => {
    let ignore = false;
    const fetchData = async () => {
      try {
        const res = await idpc.GetConfiguration({});
        if (!ignore) {
          setConfiguration(res);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchData();
    return () => {
      ignore = true;
    };
  }, []);

  const setEnhancedSecurity = useCallback(async (enabled: boolean) => {
    try {
      await idpc.SetUserEnhancedSecurity({ enabled });
      // Refresh user data to get updated state
      const res = await idpc.GetUser({});
      setUser(res.user ?? null);
    } catch (ex) {
      alert("Failed setting enhanced security: " + ex);
    }
  }, []);

  const handleEnhancedSecurityToggle = useCallback(() => {
    if (!user) return;

    const newState = !user.enhancedAuthentication;
    if (newState) {
      setPendingEnhancedSecurityState(newState);
      setEnhancedSecurityModalOpen(true);
    } else {
      setEnhancedSecurity(false);
    }
  }, [user, setEnhancedSecurity]);

  const confirmEnhancedSecurityChange = useCallback(async () => {
    await setEnhancedSecurity(pendingEnhancedSecurityState);
    setEnhancedSecurityModalOpen(false);
  }, [setEnhancedSecurity, pendingEnhancedSecurityState]);

  const getFactorCount = useCallback(() => {
    if (!user) return 0;
    return (user.totpAuthenticator?.length || 0) + (user.webauthnAuthenticator?.length || 0);
  }, [user]);

  const getEnhancedSecurityWarning = useCallback(() => {
    if (!user) return null;

    const factorCount = getFactorCount();
    const isEnabling = pendingEnhancedSecurityState;

    if (isEnabling && factorCount === 0) {
      return "Warnung: Du hast noch keine TOTP oder Passkey-Faktoren konfiguriert. Dies könnte dazu führen, dass du dich nicht mehr anmelden kannst, je nach Sicherheitsrichtlinie.";
    }

    return null;
  }, [user, pendingEnhancedSecurityState, getFactorCount]);

  if (user)
    return (
      <Nav profile={user.profile} onLogout={props.onLogout}>
        <FlexRow>
          {/* {user.profile?.picture ? (
            <ProfileIcon src={user.profile.picture}></ProfileIcon>
          ) : (
            <Avatar
              name={user?.profile?.name ?? ""}
              size={32}
              variant="marble"
            />
          )} */}
          <div style={{ alignSelf: "center", marginLeft: 0, fontSize: "1.4rem" }}>
            <div style={{ fontSize: "36px", fontWeight: 600 }}>Hallo,</div>
            {user.profile?.name}
          </div>
        </FlexRow>
        {/* <div>{user.profile?.givenName}</div>
        <div>{user.profile?.familyName}</div>
        <div>{user.profile?.gender}</div>
        <div>{user.profile?.picture}</div>
        <div>{user.profile?.locale}</div>
        <div>{user.profile?.zoneinfo}</div> */}
        <p style={{ marginTop: 40 }}>
          Du hast dich erfolgreich angemeldet. Hier verwaltest du dein LWZO-Konto: E-Mail-Adresse,
          Passwort und Sicherheitsschlüssel.
        </p>
        <div style={{ marginTop: 20, display: "flex", flexWrap: "wrap" }}>
          <a href="https://lwzo.ch/" style={{ textDecoration: "none", color: "black" }}>
            <div
              style={{
                marginRight: 30,
                marginTop: 10,
                textAlign: "center",
                borderRadius: "10%",
                boxShadow: "0px 0px 5px rgba(0, 0, 0, 0.3)",
                padding: 15,
                minWidth: 80,
                backgroundColor: "white",
              }}
            >
              <div style={{ fontWeight: 600, marginTop: 2, fontSize: "1.1rem" }}>
                Zurück zu lwzo.ch
              </div>
            </div>
          </a>
        </div>
        <hr style={{ marginTop: 20 }}></hr>
        <Title>Passwort</Title>
        {/* 02-konzept.md §4.2 sagt, ein gesetztes Passwort koste den Mail-Login.
            Das gilt hier NICHT: idp.go unterdrueckt EMAIL nur, wenn PASSWORD im
            selben Knoten derselben ACR steht. In urn:ch:lwzo:participant steht
            unter USERNAME ausschliesslich EMAIL. Ein Passwort ist fuer
            Teilnehmende darum schlicht wirkungslos - genau das steht hier. */}
        <p style={{ marginTop: 0 }}>
          Für die Anmeldung auf lwzo.ch brauchst du kein Passwort - es genügt deine
          E-Mail-Adresse. Ein Passwort benötigst du nur, wenn du zusätzlich in der
          LWZO-Geschäftsstelle oder in der Redaktion arbeitest.
        </p>
        Zuletzt geändert: {user.password?.lastChanged?.toLocaleDateString()}
        <br />
        <Button onClick={() => setPasswordModalOpen(true)}>Passwort ändern</Button>
        <PasswordChangeModal
          open={passwordModalOpen}
          onClose={() => {
            setPasswordModalOpen(false);
          }}
        />
        <Authenticator authenticate={props.authenticate} onUpdate={fetchUser} />
        <EmailManager
          user={user}
          authenticate={props.authenticate}
          onUpdate={fetchUser}
        />
        {configuration.userEnhancedSecurity?.available ? (
          <>
            <Title>Sicherheitsrichtlinen</Title>
            <div style={{ fontSize: "14px", lineHeight: "1.4" }}>
              {configuration.userEnhancedSecurity.description}
            </div>
            <div style={{ marginBottom: "10px" }}>
              <Button onClick={handleEnhancedSecurityToggle}>
                {user.enhancedAuthentication ? "Deaktivieren" : "Aktivieren"}
              </Button>
            </div>

            <ConfirmationModal
              isOpen={enhancedSecurityModalOpen}
              onClose={() => setEnhancedSecurityModalOpen(false)}
              onConfirm={confirmEnhancedSecurityChange}
              title={
                pendingEnhancedSecurityState
                  ? "Erweiterte Sicherheit aktivieren"
                  : "Erweiterte Sicherheit deaktivieren"
              }
              message={configuration.userEnhancedSecurity.description || ""}
              warningMessage={getEnhancedSecurityWarning() || undefined}
              confirmText={pendingEnhancedSecurityState ? "Aktivieren" : "Deaktivieren"}
            />
          </>
        ) : null}
      </Nav>
    );
  else
    return (
      <Nav>
        <Spinner />
      </Nav>
    );
}

const customStyles: Modal.Styles = {
  content: {
    fontFamily: "sans-serif",
    maxWidth: 500,
    position: "unset",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 10,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
};

const ModalTitle = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: space-between;
`;

const CloseButton = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  height: 100%;
  padding-top: 5px;
`;

function PasswordChangeModal(props: { open: boolean; onClose: () => void }) {
  return (
    <Modal isOpen={props.open} onRequestClose={props.onClose} style={customStyles}>
      <ModalTitle>
        <h1>Passwort ändern</h1>
        <CloseButton onClick={props.onClose}>
          <Icon path={mdiWindowClose} size={1} />
        </CloseButton>
      </ModalTitle>

      <ChangePassword
        authRecover={() => {
          props.onClose();
        }}
        reason={AuthPasswordExtension.UserRequested}
      ></ChangePassword>
    </Modal>
  );
}
